import React, { useState } from 'react';
import { ArrowLeftIcon, CheckCircleIcon, AlertCircleIcon, ClockIcon, ClipboardListIcon, PrinterIcon, CheckIcon } from 'lucide-react';
interface ProductionInstructionsProps {
  lineId: string;
  type: 'pre' | 'post';
  onComplete: () => void;
  onBack: () => void;
}
interface InstructionItem {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
}
export function ProductionInstructions({
  lineId,
  type,
  onComplete,
  onBack
}: ProductionInstructionsProps) {
  // Pre-Produktionshinweise
  const preInstructions: InstructionItem[] = [{
    id: 'pre-1',
    title: 'Arbeitsplatz reinigen und desinfizieren',
    description: 'Stellen Sie sicher, dass der Arbeitsplatz gemäß den Hygienevorschriften gereinigt ist. Desinfizieren Sie alle Arbeitsflächen und Werkzeuge, die mit den Rohstoffen in Kontakt kommen.',
    icon: <CheckCircleIcon size={24} className="text-blue-500" data-id="element-1275" />
  }, {
    id: 'pre-2',
    title: 'Persönliche Schutzausrüstung anlegen',
    description: 'Tragen Sie Handschuhe, Haarnetz und andere vorgeschriebene Schutzkleidung. Stellen Sie sicher, dass Ihre Hände gründlich gewaschen und desinfiziert sind.',
    icon: <CheckCircleIcon size={24} className="text-blue-500" data-id="element-1276" />
  }, {
    id: 'pre-3',
    title: 'Rohstoffe auf Qualität und Menge prüfen',
    description: 'Überprüfen Sie die Qualität und Menge der bereitgestellten Rohstoffe. Achten Sie auf Beschädigungen, Verunreinigungen oder abgelaufene Haltbarkeitsdaten.',
    icon: <ClockIcon size={24} className="text-blue-500" data-id="element-1277" />
  }, {
    id: 'pre-4',
    title: 'Temperaturkontrolle durchführen',
    description: 'Stellen Sie sicher, dass die TK-Rohstoffe die vorgeschriebene Temperatur haben. Dokumentieren Sie die gemessenen Temperaturen im Produktionsprotokoll.',
    icon: <AlertCircleIcon size={24} className="text-blue-500" data-id="element-1278" />
  }];
  // Post-Produktionshinweise
  const postInstructions: InstructionItem[] = [{
    id: 'post-1',
    title: 'Arbeitsbereich reinigen',
    description: 'Reinigen Sie den Arbeitsbereich nach Abschluss der Produktion gründlich. Entsorgen Sie alle Abfälle entsprechend der Vorschriften und hinterlassen Sie den Arbeitsplatz sauber.',
    icon: <CheckCircleIcon size={24} className="text-purple-500" data-id="element-1279" />
  }, {
    id: 'post-2',
    title: 'Produktionsbericht ausfüllen',
    description: 'Dokumentieren Sie alle wichtigen Informationen im Produktionsbericht. Achten Sie darauf, alle Mengen, Zeiten und besonderen Vorkommnisse zu notieren.',
    icon: <ClipboardListIcon size={24} className="text-purple-500" data-id="element-1280" />
  }, {
    id: 'post-3',
    title: 'Abweichungen melden',
    description: 'Melden Sie alle Abweichungen oder Probleme, die während der Produktion aufgetreten sind. Informieren Sie Ihren Vorgesetzten über eventuelle Qualitätsprobleme oder Prozessstörungen.',
    icon: <AlertCircleIcon size={24} className="text-purple-500" data-id="element-1281" />
  }, {
    id: 'post-4',
    title: 'Etiketten und Dokumentation prüfen',
    description: 'Stellen Sie sicher, dass alle Etiketten korrekt angebracht und alle Dokumente vollständig sind. Überprüfen Sie, ob alle produzierten Einheiten ordnungsgemäß gekennzeichnet wurden.',
    icon: <PrinterIcon size={24} className="text-purple-500" data-id="element-1282" />
  }];
  // Aktuelle Anweisungen basierend auf dem Typ (pre/post)
  const currentInstructions = type === 'pre' ? preInstructions : postInstructions;
  // State für bestätigte Anweisungen
  const [confirmedInstructions, setConfirmedInstructions] = useState<string[]>([]);
  // Funktion zum Umschalten der Bestätigung einer Anweisung
  const toggleInstructionConfirmation = (id: string) => {
    if (confirmedInstructions.includes(id)) {
      setConfirmedInstructions(confirmedInstructions.filter(item => item !== id));
    } else {
      setConfirmedInstructions([...confirmedInstructions, id]);
    }
  };
  // Prüfen, ob alle Anweisungen bestätigt wurden
  const allConfirmed = confirmedInstructions.length === currentInstructions.length;
  const getLineName = (id: string) => {
    const lineNames: {
      [key: string]: string;
    } = {
      tk1: 'TK Linie 1',
      tk2: 'TK Linie 2',
      tk3: 'TK Linie 3',
      tk4: 'TK Linie 4'
    };
    return lineNames[id] || id;
  };
  const isPre = type === 'pre';
  const title = isPre ? 'Vor der Produktion' : 'Nach der Produktion';
  const bgColor = isPre ? 'bg-blue-50' : 'bg-purple-50';
  const borderColor = isPre ? 'border-blue-200' : 'border-purple-200';
  const textColor = isPre ? 'text-blue-800' : 'text-purple-800';
  const buttonColor = isPre ? 'bg-blue-500 hover:bg-blue-600' : 'bg-purple-500 hover:bg-purple-600';
  return <div className="container mx-auto" data-id="element-1283">
      <div className="mb-6" data-id="element-1284">
        <div className="flex items-center justify-between" data-id="element-1285">
          <div data-id="element-1286">
            <h2 className="text-2xl font-bold text-gray-800 mb-2" data-id="element-1287">
              {getLineName(lineId)}
            </h2>
            <p className="text-gray-600" data-id="element-1288">Hinweise {title}</p>
          </div>
          <button onClick={onBack} className="flex items-center gap-1 px-3 py-1.5 rounded-md text-gray-700" data-id="element-1289">
            <ArrowLeftIcon size={16} data-id="element-1290" />
            <span data-id="element-1291">Zurück</span>
          </button>
        </div>
      </div>
      <div className={`bg-white rounded-xl shadow-md p-8 ${bgColor} border-2 ${borderColor}`} data-id="element-1292">
        <h3 className={`text-xl font-bold ${textColor} mb-2 text-center`} data-id="element-1293">
          Hinweise {title}
        </h3>
        <p className="text-center text-gray-600 mb-6" data-id="element-1294">
          Bitte bestätigen Sie jeden Punkt durch Anklicken
        </p>
        <div className="mb-4 text-center" data-id="element-1295">
          <span className={`font-medium ${textColor}`} data-id="element-1296">
            {confirmedInstructions.length} von {currentInstructions.length}{' '}
            Punkten bestätigt
          </span>
          <div className="w-full bg-gray-200 rounded-full h-2.5 mt-2" data-id="element-1297">
            <div className={`h-2.5 rounded-full ${isPre ? 'bg-blue-500' : 'bg-purple-500'}`} style={{
            width: `${confirmedInstructions.length / currentInstructions.length * 100}%`
          }} data-id="element-1298"></div>
          </div>
        </div>
        <div className="space-y-4 max-w-3xl mx-auto" data-id="element-1299">
          {currentInstructions.map(instruction => {
          const isConfirmed = confirmedInstructions.includes(instruction.id);
          return <div key={instruction.id} className={`flex p-4 border rounded-md ${bgColor} cursor-pointer transition-colors ${isConfirmed ? 'border-green-500 bg-green-50' : `border-gray-300 hover:border-${isPre ? 'blue' : 'purple'}-300`}`} onClick={() => toggleInstructionConfirmation(instruction.id)} data-id="element-1300">
                <div className="mr-4 flex-shrink-0 mt-1" data-id="element-1301">
                  {instruction.icon}
                </div>
                <div className="flex-1" data-id="element-1302">
                  <div className="flex items-center justify-between" data-id="element-1303">
                    <p className="font-medium text-lg" data-id="element-1304">{instruction.title}</p>
                    {isConfirmed && <span className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs font-medium flex items-center" data-id="element-1305">
                        <CheckIcon size={12} className="mr-1" data-id="element-1306" />
                        Bestätigt
                      </span>}
                  </div>
                  <p className="text-gray-600" data-id="element-1307">{instruction.description}</p>
                </div>
              </div>;
        })}
        </div>
        <div className="mt-8 text-center" data-id="element-1308">
          <button onClick={onComplete} disabled={!allConfirmed} className={`px-6 py-3 ${buttonColor} text-white rounded-lg font-semibold transition-colors ${!allConfirmed ? 'opacity-50 cursor-not-allowed' : ''}`} data-id="element-1309">
            {isPre ? 'Verstanden - Mit Produktion beginnen' : 'Abschließen - Zurück zur Übersicht'}
          </button>
          {!allConfirmed && <p className="text-sm text-gray-500 mt-2" data-id="element-1310">
              Bitte bestätigen Sie alle Hinweise, um fortzufahren
            </p>}
        </div>
      </div>
    </div>;
}